import { useMutation, useQuery } from "@tanstack/react-query";
import { useRef, useState, type FormEvent } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { useEndpoints } from "../../api/ApiContext";
import { ApiError } from "../../api/errors";
import type {
  CheckoutSessionStatus,
  DepositHistoryRow,
  DepositList,
  DepositState,
  PartyReceivable,
  PromoGrantStatus,
  PromoHistoryLine,
  WalletCurrencyRow,
  WalletSummary,
} from "../../api/types";
import { RequireMfa } from "../../auth/guards";
import { CursorListView } from "../../components/CursorListView";
import {
  Banner,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  Money,
  MoneyStat,
  PageHeader,
  QueryView,
  RefId,
  SkeletonBlock,
  StatusPill,
  Table,
  type Tone,
} from "../../components/ui";
import { formatLocal } from "../../lib/datetime";
import { externalNav } from "../../lib/externalNav";
import { newIdempotencyKey } from "../../lib/idempotency";
import { amountValidationMessage, isZeroDecimal, validateAmountInput } from "../../lib/money";
import { readStorage, writeStorage } from "../../lib/storage";
import { useCursorList } from "../../lib/useCursorList";

// UX-DESIGN.md §2.2 Wallet & deposits (migration 0056, wallet.go):
//   GET  /v1/wallet                               balances + spend freeze (G-23)
//   GET  /v1/wallet/deposits                      deposit history incl. checkouts (G-24)
//   POST /v1/wallet/deposits/intent               → Stripe Checkout (returns checkout_session_id)
//   GET  /v1/wallet/deposits/checkout/{session}   the return page's status (G-27)
// The balance is never shown as credited from the redirect: it is credited
// only by the webhook -> inbox -> reconciliation path.
//
// Migration 0067 adds `receivables` to GET /v1/wallet: every case the
// advertiser owes on (a lost chargeback or a double-payment correction),
// with a Stripe Checkout repayment path (POST
// /v1/receivables/{case}/payment-intent) for the collectible ones. A
// publisher_debt case never reaches an advertiser's wallet.

const CURRENCY = "USD"; // core-platform's operating currency (config.go VADS_OPERATING_CURRENCY)
const LAST_CHECKOUT_KEY = "vads.lastCheckoutSession";

export function WalletPage() {
  return (
    <div className="stack-lg">
      <PageHeader title="Wallet" description="Funds you've added, what's reserved against live campaigns, and what's held." />
      <RequireMfa>
        <WalletBody />
      </RequireMfa>
    </div>
  );
}

function WalletBody() {
  const ep = useEndpoints();
  const wallet = useQuery({ queryKey: ["wallet"], queryFn: () => ep.advertiser.getWallet() });
  const [amount, setAmount] = useState("");
  const addFundsRef = useRef<HTMLDivElement>(null);

  function retry(row: DepositHistoryRow) {
    setAmount(trimScale(row.amount));
    addFundsRef.current?.scrollIntoView?.({ block: "center" });
    addFundsRef.current?.querySelector("input")?.focus();
  }

  return (
    <>
      <Card
        title="Balances"
        subtitle="Cash you deposited — refundable. Spendable, reserved and held are shown separately, never netted into one number. Promotional credit is separate, below."
      >
        <QueryView query={wallet} loadingLabel="Loading balances" lines={4}>
          {(w: WalletSummary) => <Balances w={w} />}
        </QueryView>
      </Card>
      <PromoCreditCard />
      {wallet.data?.receivables.length ? <ReceivablesCard receivables={wallet.data.receivables} /> : null}
      <div ref={addFundsRef}>
        <AddFundsCard amount={amount} setAmount={setAmount} />
      </div>
      <DepositHistoryCard onRetry={retry} />
      <p className="small">
        Need money back? <Link to="/advertiser/refunds">Request a refund</Link> — refunds always return to your original payment
        method, and only ever cover cash you deposited, never promotional credit.
      </p>
    </>
  );
}

/** "250.0000000000" → "250": the exact value without numeric(28,10) padding. */
function trimScale(v: string): string {
  return v.includes(".") ? v.replace(/0+$/, "").replace(/\.$/, "") : v;
}

function Balances({ w }: { w: WalletSummary }) {
  return (
    <div className="stack">
      <span className="small muted">
        As of {new Date(w.as_of).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}. Live ledger balances.
      </span>
      {w.by_currency.map((r) => (
        <CurrencyBalances key={r.currency} r={r} />
      ))}
    </div>
  );
}

function CurrencyBalances({ r }: { r: WalletCurrencyRow }) {
  const owed = !isZeroDecimal(r.receivable);
  return (
    <div className="stack">
      <div className="grid-auto">
        <MoneyStat label="Spendable" value={r.spendable} currency={r.currency} note="Cash, available for new reservations — spent before promotional credit" />
        <MoneyStat label="Reserved" value={r.reserved} currency={r.currency} note="Earmarked against live campaigns" alwaysFlag />
        <MoneyStat label="Held" value={r.held} currency={r.currency} note="Held, e.g. by an unresolved dispute" />
        <MoneyStat label="Refund in progress" value={r.refund_pending} currency={r.currency} note="Set aside for refunds with the provider" />
      </div>
      <dl className="definition-list">
        <dt>Wallet total</dt>
        <dd>
          <Money value={r.wallet_liability_total} currency={r.currency} />{" "}
          <span className="xs muted">spendable + reserved + held + refund in progress: what we hold for you</span>
        </dd>
        <dt>Amount owed to the platform</dt>
        <dd>
          <Money value={r.receivable} currency={r.currency} />{" "}
          {owed ? <span className="xs muted">from a chargeback that your wallet didn't cover</span> : null}
        </dd>
        <dt>Net position</dt>
        <dd>
          <Money value={r.net_position} currency={r.currency} />{" "}
          <span className="xs muted">wallet total − amount owed: the one place the two are combined</span>
        </dd>
      </dl>
      <div className="stat" style={{ borderStyle: "dashed" }}>
        <span className="stat-label">Expected incoming funds</span>
        <span className="stat-value">
          <Money value={r.expected_incoming} currency={r.currency} alwaysFlag />
        </span>
        <span className="stat-note">Checkouts not yet confirmed by our payment provider. Not in any total above until they settle.</span>
      </div>
    </div>
  );
}

const PROMO_GRANT_STATUS: Record<PromoGrantStatus, { tone: Tone; label: string }> = {
  active: { tone: "success", label: "Active" },
  expired: { tone: "neutral", label: "Expired" },
  clawed_back: { tone: "warning", label: "Removed (deposit refunded or disputed)" },
  forfeited: { tone: "neutral", label: "Forfeited (account closed)" },
};

const PROMO_HISTORY: Record<PromoHistoryLine["kind"], string> = {
  grant: "Credit added",
  spend: "Spent on ads",
  held: "Held by a live ad",
  expire: "Expired",
  clawback: "Removed: qualifying deposit refunded or disputed",
  forfeit: "Forfeited on account closure",
};

/**
 * Migration 0075: promotional credit, never part of the cash balances
 * above. It is spent only after cash, can't be refunded, withdrawn or paid
 * out, expires, and is removed if the deposit that earned it is refunded
 * or charged back.
 */
function PromoCreditCard() {
  const ep = useEndpoints();
  const q = useQuery({ queryKey: ["promo-credit"], queryFn: () => ep.advertiser.getPromoCredit(), retry: false });
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);
  const redeem = useMutation({
    mutationFn: (c: string) => ep.advertiser.redeemPromoCode(c),
    onSuccess: () => {
      setCode("");
      void q.refetch();
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const c = code.trim();
    if (!c) return setCodeError("Enter a promotion code.");
    setCodeError(null);
    redeem.mutate(c);
  }

  if (q.isError) {
    return (
      <Card title="Promotional credit">
        <p className="small muted">Promotional credit couldn't be loaded right now.</p>
      </Card>
    );
  }
  const s = q.data;
  return (
    <Card
      title="Promotional credit"
      subtitle="Non-refundable ad credit from VADS promotions. Spent only after your cash; it can't be refunded, withdrawn or paid out, and it expires."
    >
      {!s ? (
        <SkeletonBlock label="Loading promotional credit" lines={2} />
      ) : (
        <div className="stack">
          <div className="grid-auto">
            <MoneyStat label="Promotional credit available" value={s.available_total} currency={s.currency} note="Used after your cash balance runs out" />
            <MoneyStat label="Held by live ads" value={s.in_flight_total} currency={s.currency} note="Returns to the credit it came from if the ad doesn't bill" />
          </div>
          {s.grants.length ? (
            <Table
              caption="Promotional credit grants"
              rows={s.grants}
              rowKey={(g) => g.grant_id}
              columns={[
                { key: "promo", header: "Promotion", cell: (g) => g.promotion },
                { key: "amt", header: "Granted", numeric: true, cell: (g) => <Money value={g.amount} currency={s.currency} /> },
                { key: "left", header: "Available", numeric: true, cell: (g) => <Money value={g.remaining} currency={s.currency} /> },
                { key: "exp", header: "Expires", cell: (g) => formatLocal(g.expires_at) },
                {
                  key: "st",
                  header: "Status",
                  cell: (g) => {
                    const st = PROMO_GRANT_STATUS[g.status] ?? { tone: "neutral" as Tone, label: g.status };
                    return <StatusPill tone={st.tone}>{st.label}</StatusPill>;
                  },
                },
              ]}
            />
          ) : (
            <p className="small muted">No promotional credit yet.</p>
          )}
          {s.pending_codes.map((p) => (
            <Banner key={p.code} tone="info" title={`Code ${p.code} is waiting for a deposit`}>
              {p.promotion}: add at least <Money value={p.min_deposit_amount} currency={s.currency} /> and the credit is added once the payment is
              confirmed.
            </Banner>
          ))}
          <form className="row" style={{ gap: "var(--space-2)", alignItems: "flex-end" }} onSubmit={submit} noValidate>
            <div style={{ maxWidth: 240 }}>
              <Input
                label="Promotion code"
                optional
                value={code}
                onChange={(e) => setCode(e.target.value)}
                error={codeError}
                hint="Enter it before you add funds: it applies to your next qualifying deposit."
              />
            </div>
            <Button type="submit" size="sm" variant="secondary" loading={redeem.isPending}>
              Apply code
            </Button>
          </form>
          {redeem.isError ? (
            <Banner tone="error" live="alert">
              {redeem.error instanceof ApiError ? promoCodeMessage(redeem.error.code, redeem.error.message) : String(redeem.error)}
            </Banner>
          ) : null}
          {s.history.length ? (
            <Table
              caption="Promotional credit history"
              rows={s.history}
              rowKey={(h) => `${h.kind}-${h.at}-${h.grant_id ?? h.reservation_id ?? ""}`}
              columns={[
                { key: "at", header: "When", cell: (h) => formatLocal(h.at) },
                { key: "what", header: "What", cell: (h) => PROMO_HISTORY[h.kind] ?? h.kind },
                { key: "amt", header: "Amount", numeric: true, cell: (h) => <Money value={h.amount} currency={s.currency} /> },
                { key: "ref", header: "Promotion or campaign", cell: (h) => h.promotion ?? (h.campaign_id ? <Link to={`/advertiser/campaigns/${h.campaign_id}`}><code>{h.campaign_id.slice(0, 8)}</code></Link> : "—") },
              ]}
            />
          ) : null}
          <p className="xs muted">{s.terms}</p>
        </div>
      )}
    </Card>
  );
}

function promoCodeMessage(code: string | undefined, fallback: string): string {
  switch (code) {
    case "promo_code_unknown":
      return "That code isn't recognised. Check it and try again.";
    case "promotion_not_live":
      return "That promotion isn't running right now.";
    case "promo_limit_reached":
      return "You've already received this promotion.";
    default:
      return fallback;
  }
}

/** Migration 0067: every receivable case the advertiser owes on, oldest first, with a Stripe repayment path for the collectible ones. */
function ReceivablesCard({ receivables }: { receivables: PartyReceivable[] }) {
  return (
    <Card title="Amounts owed" subtitle="From a lost chargeback or a double-payment correction. Your next deposit settles this first, automatically.">
      <div className="stack">
        {receivables.map((r) => (
          <ReceivableRow key={r.case_id} r={r} />
        ))}
      </div>
    </Card>
  );
}

function ReceivableRow({ r }: { r: PartyReceivable }) {
  const ep = useEndpoints();
  const [amount, setAmount] = useState("");
  const [amountError, setAmountError] = useState<string | null>(null);
  const [intent, setIntent] = useState<{ amount: string; key: string } | null>(null);
  const m = useMutation({
    mutationFn: ({ value, key }: { value: string | undefined; key: string }) => ep.advertiser.payReceivable(r.case_id, value, key),
    onSuccess: (res) => {
      writeStorage("session", LAST_CHECKOUT_KEY, res.checkout_session_id);
      externalNav.go(res.checkout_url);
    },
  });
  const paid = isZeroDecimal(r.outstanding);
  const payable = r.receivable_kind === "advertiser_receivable" && r.collectible && !paid;

  function submitFull() {
    const key = intent && intent.amount === "" ? intent.key : newIdempotencyKey();
    setIntent({ amount: "", key });
    m.mutate({ value: undefined, key });
  }

  function submitPartial(e: FormEvent) {
    e.preventDefault();
    const v = validateAmountInput(amount, r.currency);
    if (!v.ok) return setAmountError(amountValidationMessage(v.reason, r.currency));
    setAmountError(null);
    const key = intent && intent.amount === v.value ? intent.key : newIdempotencyKey();
    setIntent({ amount: v.value, key });
    m.mutate({ value: v.value, key });
  }

  return (
    <div className="stack-sm" style={{ borderTop: "1px solid var(--border)", paddingTop: "var(--space-2)" }}>
      <div className="row">
        <span>
          <Money value={r.outstanding} currency={r.currency} /> outstanding of <Money value={r.recognized} currency={r.currency} />
        </span>
        {paid ? <StatusPill tone="success">Paid</StatusPill> : !r.collectible ? <StatusPill tone="neutral">Locked (open chargeback)</StatusPill> : null}
      </div>
      <span className="xs muted">
        Case <code>{r.case_id.slice(0, 8)}</code> · opened {formatLocal(r.opened_at)}
      </span>
      {payable ? (
        <>
          <div className="row" style={{ gap: "var(--space-2)" }}>
            <Button size="sm" onClick={submitFull} loading={m.isPending}>
              Pay <Money value={r.outstanding} currency={r.currency} /> now
            </Button>
          </div>
          <form className="row" style={{ gap: "var(--space-2)", alignItems: "flex-end" }} onSubmit={submitPartial} noValidate>
            <div style={{ maxWidth: 200 }}>
              <Input label="Pay a different amount" optional inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} error={amountError} />
            </div>
            <Button type="submit" size="sm" variant="secondary" loading={m.isPending}>
              Pay this amount
            </Button>
          </form>
        </>
      ) : null}
      {m.isError ? (
        <Banner tone="error" live="alert">
          <div className="stack-sm">
            <span>{m.error.message}</span>
            {m.error instanceof ApiError && m.error.idempotencyKey ? <RefId value={m.error.idempotencyKey} /> : null}
          </div>
        </Banner>
      ) : null}
      {r.collections.length ? (
        <Table
          caption="Collections on this case"
          captionHidden
          rows={r.collections}
          rowKey={(c) => c.collection_id}
          columns={[
            { key: "at", header: "When", cell: (c) => formatLocal(c.collected_at) },
            { key: "amt", header: "Amount", numeric: true, cell: (c) => <Money value={c.amount} currency={r.currency} /> },
            { key: "how", header: "Method", cell: (c) => c.method.replace(/_/g, " ") },
            { key: "after", header: "Outstanding after", numeric: true, cell: (c) => <Money value={c.outstanding_after} currency={r.currency} /> },
          ]}
        />
      ) : null}
    </div>
  );
}

/**
 * UX-DESIGN.md §2.2 spend-freeze banner: persistent, non-dismissible, on
 * every advertiser screen (AppShell renders it). Silent unless the account
 * is frozen; a failed read shows nothing rather than a guess.
 */
export function SpendFreezeBanner() {
  const ep = useEndpoints();
  const q = useQuery({ queryKey: ["wallet"], queryFn: () => ep.advertiser.getWallet(), retry: false, staleTime: 60_000 });
  const w = q.data;
  if (!w?.spend_frozen) return null;
  const f = w.frozen;
  return (
    <div style={{ marginBottom: "var(--space-5)" }}>
    <Banner tone="error" title="Spending is frozen on this account">
      <div className="stack-sm">
        <span>
          New reservations are blocked, so your campaigns can't serve new ads — this isn't a fill-rate problem.
          {f?.reason === "open_chargeback" ? " A payment on one of your deposits is being disputed with the card issuer." : ""}
        </span>
        {f && f.campaign_ids.length ? (
          <span>
            Affected campaigns:{" "}
            {f.campaign_ids.map((id, i) => (
              <span key={id}>
                {i ? ", " : ""}
                <Link to={`/advertiser/campaigns/${id}`}>
                  <code>{id.slice(0, 8)}</code>
                </Link>
              </span>
            ))}
          </span>
        ) : null}
        <span>
          You can still view reporting, add funds, and request a refund on a deposit that isn't disputed. You can't activate campaigns or
          increase spend.
        </span>
        <span>
          <strong>To lift it:</strong>{" "}
          {f?.resolution === "lifts_when_all_chargebacks_close"
            ? "the freeze lifts when every open chargeback is resolved."
            : "contact support — the reason for this freeze isn't recorded in a form we can show."}
        </span>
      </div>
    </Banner>
    </div>
  );
}

function AddFundsCard({ amount, setAmount }: { amount: string; setAmount: (v: string) => void }) {
  const ep = useEndpoints();
  const [amountError, setAmountError] = useState<string | null>(null);
  // One key per intended deposit: resubmitting the same amount reuses it,
  // so a double-click or a retry after a timeout returns the same Checkout
  // Session instead of opening a second one (wallet.go:37-50, 0056).
  const [intent, setIntent] = useState<{ amount: string; key: string } | null>(null);

  const m = useMutation({
    mutationFn: ({ value, key }: { value: string; key: string }) => ep.advertiser.createDepositIntent(value, key),
    onSuccess: (res) => {
      writeStorage("session", LAST_CHECKOUT_KEY, res.checkout_session_id);
      window.location.assign(res.checkout_url);
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const v = validateAmountInput(amount, CURRENCY);
    if (!v.ok) {
      setAmountError(amountValidationMessage(v.reason, CURRENCY));
      return;
    }
    setAmountError(null);
    const key = intent && intent.amount === v.value ? intent.key : newIdempotencyKey();
    setIntent({ amount: v.value, key });
    m.mutate({ value: v.value, key });
  }

  const err = m.error;
  return (
    <Card title="Add funds" subtitle="You'll pay on our payment provider's secure checkout page, then come back here.">
      <form className="stack" onSubmit={submit} noValidate>
        <div style={{ maxWidth: 320 }}>
          <Input
            label={`Amount (${CURRENCY})`}
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            error={amountError}
            hint="Up to 2 decimal places."
          />
        </div>
        {err ? (
          <Banner tone={err instanceof ApiError && err.kind === "unavailable" ? "neutral" : "error"} live="alert">
            <div className="stack-sm">
              <span>{err.message}</span>
              {err instanceof ApiError && err.idempotencyKey ? <RefId value={err.idempotencyKey} /> : null}
              {err instanceof ApiError && err.retryable ? (
                <span className="small">Trying again with the same amount won't open a second checkout.</span>
              ) : null}
            </div>
          </Banner>
        ) : null}
        <div>
          <Button type="submit" loading={m.isPending || m.isSuccess}>
            Continue to checkout
          </Button>
        </div>
        <p className="xs muted">
          Your balance updates only after the payment provider confirms the payment — usually within a minute. Until then the
          deposit is expected incoming funds, not spendable.
        </p>
      </form>
    </Card>
  );
}

const DEPOSIT_STATE: Record<DepositState, { tone: Tone; label: string }> = {
  pending: { tone: "warning", label: "Pending confirmation" },
  failed: { tone: "error", label: "Failed" },
  canceled: { tone: "neutral", label: "Canceled" },
  settled: { tone: "success", label: "Settled" },
  disputed: { tone: "error", label: "Disputed" },
  reversed: { tone: "error", label: "Reversed" },
  refunded: { tone: "neutral", label: "Refunded" },
};

function DepositDetail({ d }: { d: DepositHistoryRow }) {
  switch (d.state) {
    case "pending":
      return <span className="small">Waiting for our payment provider to confirm{d.expires_at ? `; checkout expires ${formatLocal(d.expires_at)}` : ""}.</span>;
    case "failed":
      return <span className="small">The payment didn't go through. Nothing was added.</span>;
    case "canceled":
      return <span className="small">Checkout wasn't completed before it expired. Nothing was charged.</span>;
    case "disputed":
      return (
        <span className="small">
          A chargeback is open. Its effect shows in the balances above.
          {d.disputed_total && !isZeroDecimal(d.disputed_total) ? (
            <>
              {" "}
              Disputed: <Money value={d.disputed_total} currency={d.currency} />.
            </>
          ) : null}
        </span>
      );
    case "reversed":
      return (
        <span className="small">
          The chargeback was upheld and the payment reversed.
          {d.receivable_case_id ? (
            <>
              {" "}
              Amount owed case <code>{d.receivable_case_id.slice(0, 8)}</code>.
            </>
          ) : null}
        </span>
      );
    case "refunded":
      return (
        <span className="small">
          Refunded <Money value={d.refunded_total ?? "0"} currency={d.currency} /> of <Money value={d.amount} currency={d.currency} />.{" "}
          <Link to={`/advertiser/refunds?deposit=${d.deposit_id}`}>Refunds</Link>
        </span>
      );
    case "settled":
      return d.allocation ? (
        <span className="small">
          <Money value={d.allocation.spendable} currency={d.currency} /> to spendable
          {!isZeroDecimal(d.allocation.held) ? (
            <>
              , <Money value={d.allocation.held} currency={d.currency} /> held
            </>
          ) : null}
          .
        </span>
      ) : null;
  }
}

function DepositHistoryCard({ onRetry }: { onRetry: (row: DepositHistoryRow) => void }) {
  const ep = useEndpoints();
  const list = useCursorList<DepositList, DepositHistoryRow>(["deposits"], (cursor) => ep.advertiser.listDeposits(cursor), (p) => p.deposits);
  return (
    <Card title="Deposit history" subtitle="Every checkout you started, including ones that never completed.">
      <CursorListView
        list={list}
        loadingLabel="Loading deposit history"
        empty={<EmptyState icon="wallet" title="No deposits yet">Funds you add will be listed here, from checkout to settlement.</EmptyState>}
      >
        {(rows) => (
          <Table
            caption="Deposits, newest first"
            captionHidden
            rows={rows}
            rowKey={(d) => d.entry_id}
            columns={[
              { key: "at", header: "Started", cell: (d) => formatLocal(d.created_at) },
              { key: "amt", header: "Amount", cell: (d) => <Money value={d.amount} currency={d.currency} />, numeric: true },
              {
                key: "state",
                header: "Status",
                cell: (d) => {
                  const s = DEPOSIT_STATE[d.state] ?? { tone: "neutral" as Tone, label: d.state };
                  return <StatusPill tone={s.tone}>{s.label}</StatusPill>;
                },
              },
              { key: "detail", header: "Details", cell: (d) => <DepositDetail d={d} /> },
              {
                key: "act",
                header: "Action",
                cell: (d) =>
                  d.state === "failed" || d.state === "canceled" ? (
                    <Button size="sm" variant="secondary" onClick={() => onRetry(d)}>
                      Try again
                    </Button>
                  ) : d.deposit_id && (d.state === "settled" || d.state === "refunded") ? (
                    <Link to={`/advertiser/refunds?deposit=${d.deposit_id}`}>Request a refund</Link>
                  ) : (
                    "—"
                  ),
              },
            ]}
          />
        )}
      </CursorListView>
    </Card>
  );
}

/**
 * Stripe Checkout return pages (success_url carries
 * session_id={CHECKOUT_SESSION_ID}; cancel_url carries nothing — wallet.go:86-87).
 * The success page polls GET /v1/wallet/deposits/checkout/{session_id}
 * until the webhook has been processed (G-27).
 */
export function DepositReturnPage() {
  const { outcome } = useParams();
  const [params] = useSearchParams();
  const success = outcome === "success";
  // The success URL names the session; the intent's own checkout_session_id
  // (kept in this tab when checkout started) is the fallback.
  const sessionId = success ? (params.get("session_id") ?? readStorage("session", LAST_CHECKOUT_KEY)) : null;
  return (
    <div className="stack-lg">
      <PageHeader title={success ? "Payment submitted" : "Checkout canceled"} back={{ to: "/advertiser/wallet", label: "Wallet" }} />
      {success ? null : (
        <Banner tone="neutral" title="Nothing was charged" live="polite">
          You left checkout before paying. You can start again whenever you're ready.
        </Banner>
      )}
      {success && sessionId ? <CheckoutStatus sessionId={sessionId} /> : null}
      {success && !sessionId ? (
        <Banner tone="info" title="Pending confirmation" live="polite">
          Thanks — your payment was submitted. It shows as pending in your deposit history until our payment provider confirms it, then your
          spendable balance updates.
        </Banner>
      ) : null}
      <div className="row">
        <Link className="btn btn-primary" to="/advertiser/wallet">
          {success ? "Back to wallet" : "Try again"}
        </Link>
      </div>
    </div>
  );
}

const POLL_MS = 3_000;

function CheckoutStatus({ sessionId }: { sessionId: string }) {
  const ep = useEndpoints();
  const q = useQuery({
    queryKey: ["checkout-session", sessionId],
    queryFn: () => ep.advertiser.getCheckoutSession(sessionId),
    refetchInterval: (query) => (query.state.data?.state === "pending" ? POLL_MS : false),
    retry: false,
  });
  if (q.isPending) return <SkeletonBlock label="Checking your payment" lines={2} />;
  if (q.isError) {
    if (q.error instanceof ApiError && q.error.code === "checkout_session_not_found") {
      return (
        <Banner tone="info" title="Pending confirmation" live="polite">
          We don't have a record of this checkout yet. If you paid, it will appear in your deposit history once our payment provider confirms
          it.
        </Banner>
      );
    }
    return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  }
  return <CheckoutState s={q.data} />;
}

function CheckoutState({ s }: { s: CheckoutSessionStatus }) {
  switch (s.state) {
    case "pending":
      return (
        <Banner tone="info" title="Pending confirmation" live="polite">
          Thanks — your payment of <Money value={s.amount} currency={s.currency} /> was submitted. It shows as pending until our payment
          provider confirms it, then your spendable balance updates. We never show it as credited before that confirmation arrives. This page
          checks again every few seconds.
        </Banner>
      );
    case "settled":
      return (
        <Banner tone="success" title="Payment confirmed" live="polite">
          <Money value={s.amount} currency={s.currency} /> was added to your spendable balance
          {s.resolved_at ? ` at ${formatLocal(s.resolved_at)}` : ""}.
        </Banner>
      );
    case "failed":
      return (
        <Banner tone="error" title="The payment didn't go through" live="polite">
          Our payment provider reported that this payment failed. Nothing was added to your wallet. You can try again from the wallet.
        </Banner>
      );
    case "canceled":
      return (
        <Banner tone="neutral" title="Checkout expired" live="polite">
          This checkout expired before it was completed. Nothing was charged.
        </Banner>
      );
  }
}
